import { Button } from './ui/button';
import { ArrowRight, CheckCircle2, Shield, Star, Users, Clock, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Reveal } from './motion/Reveal';
import { HeroVideo } from './HeroVideo';

const MotionButton = motion(Button);

const PreviewCard = () => (
  <div className="relative w-full max-w-sm mx-auto mt-6 lg:mt-0">
    <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-4 sm:p-7 border border-slate-100">
      <div className="flex items-center gap-2.5 sm:gap-3 mb-3.5 sm:mb-5">
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-600 rounded-lg sm:rounded-xl flex items-center justify-center shrink-0 shadow-md shadow-red-600/30">
          <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </div>
        <div>
          <p className="font-extrabold text-slate-900 text-xs sm:text-sm leading-tight">O que a empresa deixou de pagar</p>
          <p className="text-slate-400 text-[10px] sm:text-xs font-medium">Conta feita na hora</p>
        </div>
      </div>

      <div className="space-y-2 sm:space-y-3">
        {[
          { label: 'Horas extras trabalhadas de graça', tag: 'A Receber' },
          { label: 'FGTS e multa de 40% presos', tag: 'A Receber' },
          { label: 'Acerto parcelado ou pago pela metade', tag: 'A Receber' },
          { label: 'Almoço engolido em 15 minutos', tag: 'A Receber' },
        ].map((item) => (
          <div
            key={item.label}
            className="flex items-center gap-2 sm:gap-3 bg-red-50/70 border border-red-100/80 rounded-lg sm:rounded-xl px-3 py-2 sm:px-4 sm:py-3"
          >
            <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600 shrink-0" />
            <span className="text-[11px] sm:text-sm font-bold text-slate-800 leading-snug">{item.label}</span>
            <span className="ml-auto text-[9px] sm:text-xs font-black text-red-600 uppercase tracking-wider shrink-0 bg-red-100/80 px-1.5 py-0.5 rounded">
              {item.tag}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-3 pt-2.5 sm:pt-3 border-t border-slate-100 flex items-center justify-between text-[10px] sm:text-[11px] text-slate-500">
        <span>Prazo legal: 2 anos</span>
        <span className="text-red-600 font-extrabold flex items-center gap-1">
          ⏳ O prazo está correndo
        </span>
      </div>
    </div>

    {/* Selo flutuante de nota */}
    <div className="absolute -bottom-3 -left-2 sm:-bottom-5 sm:-left-6 bg-white rounded-xl shadow-lg px-2.5 py-1.5 sm:px-4 sm:py-2 flex items-center gap-1.5 border border-slate-100">
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-yellow-400 text-yellow-400" />
        ))}
      </div>
      <span className="font-extrabold text-slate-800 text-[11px] sm:text-sm">4.9</span>
      <span className="text-slate-400 text-[9px] sm:text-xs font-medium">· +R$ 28M recuperados</span>
    </div>
  </div>
);

export const Hero = ({ video = false }) => {
  const scrollToContact = () => {
    const el = document.getElementById('contato');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative flex items-center pt-5 sm:pt-10 md:pt-12 pb-10 sm:pb-16 overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800"
    >
      {/* Decorative blurry elements */}
      <div className="absolute top-4 -right-10 w-40 h-40 sm:w-72 sm:h-72 bg-white/5 rounded-full blur-2xl pointer-events-none" />
      <div className="absolute bottom-10 -left-10 w-48 h-48 sm:w-96 sm:h-96 bg-blue-400/10 rounded-full blur-2xl pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-6 sm:gap-10 lg:gap-8 items-center">

          {/* Coluna de texto */}
          <div className="text-center lg:text-left">
            <Reveal mode="mount" delay={0.1} className="flex justify-center lg:justify-start mb-3 sm:mb-5">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-3 py-1 rounded-full text-[11px] sm:text-xs font-medium">
                <span className="w-2 h-2 rounded-full bg-red-400 animate-ping" />
                <span className="font-bold text-yellow-300">A gente defende quem trabalha</span>
                <div className="w-px h-3 bg-white/30" />
                <span className="text-white/90">Zero custo inicial</span>
              </div>
            </Reveal>

            {/* H1 — quebra orgânica e sem sublinhado quebrado no mobile */}
            <Reveal mode="mount"
              as="h1"
              data-testid="hero-title"
              delay={0.2}
              className="text-[1.65rem] sm:text-4xl md:text-5xl lg:text-[3.25rem] font-black text-white leading-[1.2] sm:leading-[1.12] tracking-tight mb-3 sm:mb-5 max-w-lg mx-auto lg:mx-0"
            >
              Trabalhou sem carteira assinada?{' '}
              <span className="text-yellow-300">
                A conta que ficou pra trás é grande.
              </span>
            </Reveal>

            {video && (
              <Reveal mode="mount" delay={0.25} className="mb-5 sm:mb-7">
                <HeroVideo nome="sim-home" />
                <p className="mt-2.5 text-white/60 text-[11px] sm:text-xs text-center lg:text-left">
                  Veja no vídeo a conta de quem trabalhou 2 anos sem registro
                </p>
              </Reveal>
            )}

            {/* SUBHEAD — espaçamento respirável */}
            <Reveal mode="mount"
              as="p"
              data-testid="hero-subtitle"
              delay={0.3}
              className="text-xs sm:text-base md:text-xl text-white/90 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-5 sm:mb-7"
            >
              Sem registro você ficou sem FGTS, sem 13º, sem férias e sem aviso. Isso tudo continua
              sendo seu e dá pra cobrar.{' '}
              <strong className="text-white font-extrabold">De graça pra começar: você só paga se o dinheiro cair na sua conta.</strong>
            </Reveal>

            {/* CTA compacto e touch-friendly */}
            <Reveal mode="mount" delay={0.4} className="flex flex-col items-center lg:items-start gap-2.5 sm:gap-3">
              <MotionButton
                data-testid="hero-cta-primary"
                onClick={scrollToContact}
                size="lg"
                whileTap={{ scale: 0.98 }}
                className="w-full sm:w-auto bg-yellow-400 hover:bg-yellow-300 text-blue-950 rounded-xl sm:rounded-full px-6 sm:px-10 py-4 sm:py-6 h-auto text-base sm:text-xl font-black shadow-lg shadow-yellow-500/30 transition-all flex items-center justify-center group"
              >
                <span>Ver quanto é meu</span>
                <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
              </MotionButton>
              <p className="text-white/80 text-[10px] sm:text-xs flex items-center gap-1.5 font-medium">
                <Clock className="w-3.5 h-3.5 text-yellow-300 shrink-0" />
                <span>Avaliação de graça · prazo de 2 anos desde a saída</span>
              </p>
            </Reveal>
          </div>

          {/* Coluna visual — quadro branco preservado, agora bem encaixado */}
          <div className={video ? 'hidden lg:block' : 'block lg:block'}>
            <PreviewCard />
          </div>
        </div>

        {/* Trust Indicators */}
        <Reveal delay={0.5} className="mt-8 sm:mt-12 md:mt-16">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 max-w-4xl mx-auto">
            <div className="flex items-center justify-center sm:justify-start gap-3 bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl px-4 py-3 sm:px-6 sm:py-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500/20 rounded-lg sm:rounded-full flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-300" />
              </div>
              <div className="text-left">
                <p className="text-white font-bold text-xs sm:text-sm">Você não gasta nada</p>
                <p className="text-white/70 text-[10px] sm:text-xs">Zero custo para começar</p>
              </div>
            </div>
            <div className="flex items-center justify-center sm:justify-start gap-3 bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl px-4 py-3 sm:px-6 sm:py-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500/20 rounded-lg sm:rounded-full flex items-center justify-center shrink-0">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-green-300" />
              </div>
              <div className="text-left">
                <p className="text-white font-bold text-xs sm:text-sm">Sem Risco Nenhum</p>
                <p className="text-white/70 text-[10px] sm:text-xs">Só paga se ganhar</p>
              </div>
            </div>
            <div className="flex items-center justify-center sm:justify-start gap-3 bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl px-4 py-3 sm:px-6 sm:py-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500/20 rounded-lg sm:rounded-full flex items-center justify-center shrink-0">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-green-300" />
              </div>
              <div className="text-left">
                <p className="text-white font-bold text-xs sm:text-sm">Segredo Absoluto</p>
                <p className="text-white/70 text-[10px] sm:text-xs">A empresa não fica sabendo</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
